// Shared button primitive. DESIGN-VISUAL.md §6.8: exactly four variants, no filled-ink button, no
// `→` appended to the label (the verb is the affordance, §5.6 rule 6), and `--tap-min` 44×44 px on
// every viewport below 1024 px. `:focus-visible` is never overridden here — the 2 px ink ring comes
// from the global rule in `frontend/src/styles/globals.css` (T-03), so every control that renders a
// real `<button>` inherits it for free.
'use client';

import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'attention';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Accent fill + ink text + 1 px ink border: the fill alone fails the 3:1 non-text boundary
  // against paper (DESIGN-VISUAL.md §3.1), so the border is not optional.
  primary: 'border border-ink bg-accent text-ink',
  secondary: 'border border-ink-soft bg-paper text-ink',
  tertiary: 'border-0 bg-transparent px-0 text-ink underline underline-offset-2',
  attention: 'border border-attention bg-paper text-attention',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Shown instead of `children` while `loading` is true. Never a spinner icon alone — a money
   * action always carries a word (DESIGN-VISUAL.md §6.8). */
  loadingLabel?: ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
}

export function Button({
  variant = 'secondary',
  loading = false,
  loadingLabel,
  fullWidth = false,
  disabled,
  className = '',
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const isTertiary = variant === 'tertiary';
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center gap-s2 rounded-control text-body font-label',
        'transition-colors duration-instant ease-standard',
        'disabled:cursor-not-allowed disabled:opacity-60',
        isTertiary ? '' : 'min-h-tap-min min-w-tap-min px-s5 py-s2',
        fullWidth ? 'w-full' : '',
        VARIANT_CLASSES[variant],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading && loadingLabel ? loadingLabel : children}
    </button>
  );
}
